import React, { useRef } from 'react';

interface SparklineProps {
  /** Illustrative data points (decorative trend, not literal analytics) */
  data: number[];
  color: string;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Decorative "stock ticker" style area/line chart. Hand-rolled SVG (no charting
 * library needed) since the data is illustrative, not an interactive dataset.
 */
const Sparkline: React.FC<SparklineProps> = ({ data, color, width = 260, height = 120, className = '' }) => {
  const gradientId = useRef(`sparkline-gradient-${Math.random().toString(36).slice(2)}`).current;
  const padding = 10;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * (width - padding * 2);
    const y = padding + (1 - (value - min) / range) * (height - padding * 2);
    return { x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;
  const lastPoint = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label="Growth trend illustration"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPoint.x} cy={lastPoint.y} r="7" fill={color} stroke="white" strokeWidth="2" />
    </svg>
  );
};

export default Sparkline;
